/**
 * Painting tool: click and click-drag to paint census blocks into the
 * active district.
 *
 * Design (per ADR-001: the map is a dumb picture, feature-state is the
 * paint):
 *  - Pointer events on the map canvas; a synchronous hit-test on
 *    pointerdown decides whether this gesture is a stroke (a block was hit)
 *    or a normal map pan (empty space) — pan is only disabled mid-stroke.
 *  - Brushes: size 0 paints the single block under the pointer; larger sizes
 *    paint every block that overlaps a circle of BRUSH_RADIUS_PX around it.
 *  - Strokes are interpolated: fast drags generate sparse pointermove
 *    events, so the segment between consecutive points is sampled at a
 *    spacing below the brush radius — no skipped blocks along the path.
 *  - During a stroke, points are queued and processed in a single
 *    requestAnimationFrame pass per frame, batched off the React render path.
 *  - "Protect other districts" (lockAssigned): paint only claims unassigned
 *    blocks (or the active district's own); erase only clears the active
 *    district. Lets people fill gaps without nibbling their neighbors.
 *  - Feature-state writes are the visual feedback (<50ms path). The diff
 *    (geoid, prev, next) is committed to the painting store once on stroke
 *    end — NO per-stroke server calls; the store debounces the bulk upsert.
 *  - Hover: the blocks under the brush get a `hover` feature-state (an
 *    outline layer draws it) and a DOM ring shows the brush footprint, so
 *    people see exactly what a click will paint before they click.
 *  - Escape cancels the in-progress stroke and reverts its visual changes.
 *
 * The tool registers a MapBridge with the painting store so undo/redo and
 * plan loads can push feature-state without holding a map reference.
 */
import maplibregl from 'maplibre-gl';
import { BLOCKS_FILL_LAYER_ID, BLOCKS_SOURCE_ID } from '../MapShell';
import {
  BRUSH_RADIUS_PX,
  usePaintingStore,
  type AssignmentChange,
  type BrushSize,
  type MapBridge,
} from '../../stores/painting';

export interface HoverInfo {
  /** Block under the pointer centre (null when off the jurisdiction). */
  geoid: string | null;
  /** Number of blocks the brush would touch. */
  count: number;
  /** Pointer position in CSS px relative to the map container. */
  x: number;
  y: number;
}

export interface PaintToolOptions {
  /** Vector source-layer name inside the blocks PMTiles archive. */
  getSourceLayer: () => string;
  /** Hover feedback for the tooltip (null when the pointer leaves). */
  onHover?: (info: HoverInfo | null) => void;
}

export interface PaintTool {
  destroy: () => void;
}

/** A queued pointer position in CSS pixels relative to the canvas. */
interface QueuedPoint {
  x: number;
  y: number;
}

function toCanvasPoint(event: PointerEvent | MouseEvent, canvas: HTMLCanvasElement): QueuedPoint {
  const rect = canvas.getBoundingClientRect();
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

function featureGeoid(feature: maplibregl.MapGeoJSONFeature | undefined): string | null {
  if (!feature) return null;
  const id = feature.id;
  if (typeof id === 'string') return id;
  if (typeof id === 'number') return String(id);
  return null;
}

/** Outer rings of a (Multi)Polygon feature, in lng/lat. */
function outerRings(feature: maplibregl.MapGeoJSONFeature): number[][][] {
  const geom = feature.geometry;
  if (geom.type === 'Polygon') return geom.coordinates.slice(0, 1) as number[][][];
  if (geom.type === 'MultiPolygon') {
    return (geom.coordinates as number[][][][]).map((poly) => poly[0] ?? []);
  }
  return [];
}

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  const tag = el?.tagName ?? '';
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!el?.isContentEditable;
}

export function createPaintTool(map: maplibregl.Map, options: PaintToolOptions): PaintTool {
  const canvas = map.getCanvas();
  const container = map.getContainer();
  let stroking = false;
  let queued: QueuedPoint[] = [];
  let lastPoint: QueuedPoint | null = null;
  let frameScheduled = false;
  let strokeDiff: AssignmentChange[] = [];
  let paintedThisStroke = new Set<string>();
  let hovered = new Set<string>();
  let hoverPoint: QueuedPoint | null = null;
  let hoverFrame = 0;

  // Brush footprint ring (pure DOM, positioned with transforms — no React).
  const ring = document.createElement('div');
  ring.setAttribute('aria-hidden', 'true');
  ring.style.cssText =
    'position:absolute;left:0;top:0;pointer-events:none;border-radius:9999px;z-index:5;' +
    'border:2px solid #1C2A33;box-shadow:0 0 0 1px rgba(255,255,255,0.85), inset 0 0 0 1px rgba(255,255,255,0.6);' +
    'display:none;will-change:transform;';
  container.appendChild(ring);

  const sourceLayer = (): string => options.getSourceLayer();

  const bridge: MapBridge = {
    applyFeatureState: (changes) => {
      const layer = sourceLayer();
      for (const change of changes) {
        map.setFeatureState(
          { source: BLOCKS_SOURCE_ID, sourceLayer: layer, id: change.geoid },
          { district: change.district },
        );
      }
    },
    clearFeatureState: () => {
      // removeFeatureState clears every key (including hover); re-mark hover.
      map.removeFeatureState({ source: BLOCKS_SOURCE_ID, sourceLayer: sourceLayer() });
      hovered = new Set<string>();
    },
  };
  usePaintingStore.getState().setBridge(bridge);

  function radiusFor(size: BrushSize): number {
    return BRUSH_RADIUS_PX[size] ?? 0;
  }

  /** GEOIDs of every block the brush covers at `point`. */
  function blocksUnderBrush(point: QueuedPoint, radius: number): string[] {
    if (radius <= 0) {
      const features = map.queryRenderedFeatures([point.x, point.y], {
        layers: [BLOCKS_FILL_LAYER_ID],
      });
      const geoid = featureGeoid(features[0]);
      return geoid ? [geoid] : [];
    }
    const ids = new Set<string>();
    // The block directly under the centre always counts (large blocks whose
    // vertices all fall outside the circle).
    const centre = featureGeoid(
      map.queryRenderedFeatures([point.x, point.y], { layers: [BLOCKS_FILL_LAYER_ID] })[0],
    );
    if (centre) ids.add(centre);
    const candidates = map.queryRenderedFeatures(
      [
        [point.x - radius, point.y - radius],
        [point.x + radius, point.y + radius],
      ],
      { layers: [BLOCKS_FILL_LAYER_ID] },
    );
    const r2 = radius * radius;
    for (const feature of candidates) {
      const geoid = featureGeoid(feature);
      if (!geoid || ids.has(geoid)) continue;
      // A block is in the brush when its centroid or any vertex is inside
      // the circle — cheap, and indistinguishable from exact overlap at
      // brush sizes.
      let hit = false;
      for (const ringCoords of outerRings(feature)) {
        let sx = 0;
        let sy = 0;
        let n = 0;
        for (const coord of ringCoords) {
          const p = map.project(coord as [number, number]);
          const dx = p.x - point.x;
          const dy = p.y - point.y;
          if (dx * dx + dy * dy <= r2) {
            hit = true;
            break;
          }
          sx += p.x;
          sy += p.y;
          n += 1;
        }
        if (hit) break;
        if (n > 0) {
          const dx = sx / n - point.x;
          const dy = sy / n - point.y;
          if (dx * dx + dy * dy <= r2) {
            hit = true;
            break;
          }
        }
      }
      if (hit) ids.add(geoid);
    }
    return [...ids];
  }

  /** Should a stroke change this block from `prev` to `next`? */
  function allowed(prev: number | null, next: number | null): boolean {
    if (prev === next) return false;
    const { lockAssigned, activeDistrict } = usePaintingStore.getState();
    if (!lockAssigned) return true;
    // Paint: only unassigned blocks. Erase: only the active district's.
    return next === null ? prev === activeDistrict : prev === null;
  }

  function paintPoint(point: QueuedPoint): void {
    const state = usePaintingStore.getState();
    // Eraser paints blocks back to unassigned (null); the diff, undo/redo,
    // and dirty tracking treat it exactly like a paint stroke.
    const next = state.paintMode === 'erase' ? null : state.activeDistrict;
    const layer = sourceLayer();
    for (const geoid of blocksUnderBrush(point, radiusFor(state.brushSize))) {
      if (paintedThisStroke.has(geoid)) continue;
      paintedThisStroke.add(geoid);
      const prev = state.assignments[geoid] ?? null;
      if (!allowed(prev, next)) continue;
      strokeDiff.push({ geoid, prev, next });
      map.setFeatureState(
        { source: BLOCKS_SOURCE_ID, sourceLayer: layer, id: geoid },
        { district: next },
      );
    }
  }

  /** Expand a raw pointer point into evenly spaced samples from the last one. */
  function interpolate(to: QueuedPoint): QueuedPoint[] {
    const from = lastPoint;
    lastPoint = to;
    if (!from) return [to];
    const radius = radiusFor(usePaintingStore.getState().brushSize);
    const spacing = Math.max(3, radius * 0.6);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.hypot(dx, dy);
    const steps = Math.min(200, Math.floor(dist / spacing));
    const out: QueuedPoint[] = [];
    for (let i = 1; i <= steps; i += 1) {
      out.push({ x: from.x + (dx * i) / (steps + 1), y: from.y + (dy * i) / (steps + 1) });
    }
    out.push(to);
    return out;
  }

  function processQueue(): void {
    frameScheduled = false;
    const points = queued;
    queued = [];
    for (const point of points) paintPoint(point);
  }

  function scheduleFrame(): void {
    if (frameScheduled) return;
    frameScheduled = true;
    requestAnimationFrame(processQueue);
  }

  /* ── Hover preview ─────────────────────────────────────────────── */

  function setHover(ids: string[]): void {
    const layer = sourceLayer();
    const nextSet = new Set(ids);
    for (const id of hovered) {
      if (!nextSet.has(id)) {
        map.setFeatureState({ source: BLOCKS_SOURCE_ID, sourceLayer: layer, id }, { hover: false });
      }
    }
    for (const id of nextSet) {
      if (!hovered.has(id)) {
        map.setFeatureState({ source: BLOCKS_SOURCE_ID, sourceLayer: layer, id }, { hover: true });
      }
    }
    hovered = nextSet;
  }

  function paintingActive(): boolean {
    const s = usePaintingStore.getState();
    return !!s.planId && (s.paintMode === 'paint' || s.paintMode === 'erase');
  }

  function updateCursor(): void {
    const s = usePaintingStore.getState();
    if (!paintingActive()) {
      canvas.style.cursor = '';
      ring.style.display = 'none';
      return;
    }
    canvas.style.cursor = s.brushSize === 0 ? 'crosshair' : 'none';
    const color = s.paintMode === 'erase' ? '#1C2A33' : (s.colors[s.activeDistrict] ?? '#1C2A33');
    ring.style.borderColor = color;
    ring.style.borderStyle = s.paintMode === 'erase' ? 'dashed' : 'solid';
  }

  function renderHover(): void {
    hoverFrame = 0;
    const point = hoverPoint;
    if (!point || !paintingActive()) {
      setHover([]);
      ring.style.display = 'none';
      options.onHover?.(null);
      return;
    }
    const s = usePaintingStore.getState();
    const radius = radiusFor(s.brushSize);
    if (radius > 0) {
      ring.style.display = 'block';
      ring.style.width = `${radius * 2}px`;
      ring.style.height = `${radius * 2}px`;
      ring.style.transform = `translate(${point.x - radius}px, ${point.y - radius}px)`;
    } else {
      ring.style.display = 'none';
    }
    const ids = blocksUnderBrush(point, radius);
    setHover(ids);
    const centre = radius > 0
      ? featureGeoid(map.queryRenderedFeatures([point.x, point.y], { layers: [BLOCKS_FILL_LAYER_ID] })[0])
      : (ids[0] ?? null);
    options.onHover?.({ geoid: centre, count: ids.length, x: point.x, y: point.y });
  }

  function scheduleHover(point: QueuedPoint | null): void {
    hoverPoint = point;
    if (hoverFrame) return;
    hoverFrame = requestAnimationFrame(renderHover);
  }

  /* ── Stroke lifecycle ──────────────────────────────────────────── */

  function hitTest(point: QueuedPoint): boolean {
    const radius = radiusFor(usePaintingStore.getState().brushSize);
    return blocksUnderBrush(point, radius).length > 0;
  }

  function beginStroke(event: PointerEvent, point: QueuedPoint): void {
    stroking = true;
    strokeDiff = [];
    paintedThisStroke = new Set<string>();
    lastPoint = null;
    queued = interpolate(point);
    map.dragPan.disable();
    canvas.setPointerCapture?.(event.pointerId);
    scheduleFrame();
  }

  function endStroke(commit: boolean): void {
    if (!stroking) return;
    stroking = false;
    // Flush any points queued after the last frame before committing.
    if (frameScheduled) {
      processQueue();
    }
    queued = [];
    lastPoint = null;
    if (map.dragPan.isEnabled() === false) map.dragPan.enable();
    const diff = strokeDiff;
    strokeDiff = [];
    paintedThisStroke = new Set<string>();
    if (!commit) {
      // Revert this stroke's visual changes (Escape).
      bridge.applyFeatureState(diff.map((c) => ({ geoid: c.geoid, district: c.prev })));
      return;
    }
    usePaintingStore.getState().commitStroke(diff);
  }

  function onPointerDown(event: PointerEvent): void {
    // Left button / touch / pen only; right-drag stays a map pan.
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    if (!paintingActive()) return;
    const point = toCanvasPoint(event, canvas);
    if (!hitTest(point)) return; // empty space: leave pan alone
    event.preventDefault();
    beginStroke(event, point);
  }

  function onPointerMove(event: PointerEvent): void {
    const point = toCanvasPoint(event, canvas);
    if (event.pointerType === 'mouse') scheduleHover(point);
    if (!stroking) return;
    event.preventDefault();
    queued.push(...interpolate(point));
    scheduleFrame();
  }

  function onPointerLeave(): void {
    scheduleHover(null);
  }

  function onPointerUp(): void {
    endStroke(true);
  }

  function onPointerCancel(): void {
    endStroke(false);
  }

  function onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      endStroke(false);
      return;
    }
    if (isTypingTarget(event.target)) return;
    const state = usePaintingStore.getState();
    if (!state.planId) return;
    const mod = event.metaKey || event.ctrlKey;
    const key = event.key.toLowerCase();

    // Undo / redo: Cmd/Ctrl+Z, Shift+Cmd/Ctrl+Z, Ctrl+Y.
    if (mod && key === 'z') {
      event.preventDefault();
      if (stroking) return;
      if (event.shiftKey) state.redo();
      else state.undo();
      return;
    }
    if (mod && key === 'y') {
      event.preventDefault();
      if (!stroking) state.redo();
      return;
    }
    if (mod || event.altKey) return;

    if (key === 'e') state.setPaintMode('erase');
    else if (key === 'b') state.setPaintMode('paint');
    else if (key === 'v' || key === 'h') state.setPaintMode('pan');
    else if (key === 'l') state.setLockAssigned(!state.lockAssigned);
    else if (key === '[') state.setBrushSize(Math.max(0, state.brushSize - 1) as BrushSize);
    else if (key === ']') state.setBrushSize(Math.min(3, state.brushSize + 1) as BrushSize);
    else if (/^[0-9]$/.test(key)) {
      // 1–9 pick districts 1–9, 0 picks district 10.
      const index = key === '0' ? 9 : Number(key) - 1;
      if (index < state.districtCount) {
        state.setActiveDistrict(index);
        if (state.paintMode !== 'paint') state.setPaintMode('paint');
      }
    } else return;
    event.preventDefault();
  }

  // Keep the cursor + ring in sync with tool changes made anywhere (toolbar,
  // keyboard, palette).
  const unsubscribe = usePaintingStore.subscribe((s, prev) => {
    if (
      s.paintMode !== prev.paintMode ||
      s.brushSize !== prev.brushSize ||
      s.activeDistrict !== prev.activeDistrict ||
      s.planId !== prev.planId ||
      s.colors !== prev.colors
    ) {
      updateCursor();
      if (hoverPoint) scheduleHover(hoverPoint);
    }
  });
  updateCursor();

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerleave', onPointerLeave);
  // pointerup can land outside the canvas after a drag; listen on window.
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerCancel);
  window.addEventListener('keydown', onKeyDown);
  // Panning/zooming moves blocks under a still pointer: refresh the preview.
  const onMove = (): void => {
    if (hoverPoint && !stroking) scheduleHover(hoverPoint);
  };
  map.on('moveend', onMove);

  return {
    destroy: () => {
      endStroke(true);
      unsubscribe();
      if (hoverFrame) cancelAnimationFrame(hoverFrame);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('keydown', onKeyDown);
      map.off('moveend', onMove);
      ring.remove();
      canvas.style.cursor = '';
      if (map.dragPan.isEnabled() === false) map.dragPan.enable();
      if (usePaintingStore.getState().bridge === bridge) {
        usePaintingStore.getState().setBridge(null);
      }
    },
  };
}
